import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, withCtx as _withCtx, isRef as _isRef, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "centered text-center",
  style: {"min-height":"200px"}
}
const _hoisted_2 = { key: 1 }
const _hoisted_3 = { key: 2 }

import { computed, onMounted, ref, watch } from 'vue';
import {
  BAlert,
  BButton,
  BCollapse,
  BSpinner,
  BTooltip,
} from 'bootstrap-vue-next';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useQuery } from '@vue/apollo-composable';
import { useVisibleQueryPolling } from '@/composables/visible-query-polling';
import {
  UploadManagerTransferStartsDocument,
  UploadManagerUserDocument,
} from '@/gql/graphql';
import UploadComponent from '@/components/upload/UploadComponent.vue';
import ComputeTaskList from '@/components/compute/ComputeTaskList.vue';
import WebUploadList from '@/components/upload/WebUploadList.vue';
import { scrollToElement } from '@/utils/view-utils';
import { formatDateTimeToIso } from '@/utils/date-utils';
import {
  getStartingTransferPlaceholder,
  TRANSFER_TASK_TYPES,
} from '@/utils/transfer-start-placeholder';
import {
  COMPUTE_TASK_TYPES,
  SHARING_USER_ROLES,
  UPLOAD_MODES,
  UploadModeType,
} from '@/constants';
import { useRouter } from 'vue-router';


export default /*@__PURE__*/_defineComponent({
  __name: 'UploadManagerView',
  setup(__props) {

const router = useRouter();

const showUploadOptions = ref(false);
const showWebUploadStarted = ref(false);
const showDataTransferStarted = ref(false);
const selectedUploadOption = ref<UploadModeType>(UPLOAD_MODES.WEB);

// --- Query User ---
const {
  result: resultUserDetails,
  loading: loadingUserDetails,
  error: errorFetchUserDetails,
  refetch: refetchUserDetails,
} = useQuery(UploadManagerUserDocument);

// --- User Organization ---
const userOrganization = computed(() => {
  const assocOrgs =
    resultUserDetails.value?.user?.organizationsAssociated || [];
  const org = assocOrgs
    .filter((assoc) => assoc && assoc.organization)
    .find((assoc2) => assoc2?.removedAt === null)?.organization;

  return org;
});
const userHasNoOrg = computed(() => {
  return !userOrganization.value;
});

// --- Query recent Compute Pipeline parents and their transfer children ---
const tasksQueryVariables = computed(() => {
  return {
    userRole: SHARING_USER_ROLES.OWNER,
    taskTypes: [COMPUTE_TASK_TYPES.COMPUTE_PIPELINE],
    createdAfter: formatDateTimeToIso(
      new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
    ),
    pagingPageCurrent: 1,
    pagingItemsPerPage: 5,
  };
});

const {
  result: tasksQueryResult,
  error: tasksQueryError,
  refetch: refetchTasks,
  onResult: onTasksQueryDone,
  onError: onTasksQueryError,
  query: taskPollingQuery,
} = useQuery(UploadManagerTransferStartsDocument, tasksQueryVariables.value);
useVisibleQueryPolling(taskPollingQuery);

onTasksQueryDone((res) => {
  console.debug('Compute Pipeline Tasks response retrieved');
  if (res.data) {
    if (res.data.computeTasks) {
      console.debug('Compute Pipeline Tasks retrieved successfuly');
    } else {
      console.error(
        'Error, could not retrieve compute pipeline tasks:',
        res.data
      );
    }
  } else if (!res.loading) {
    console.error(
      'Error, could not retrieve compute pipeline tasks result data:',
      res
    );
  }
});

onTasksQueryError((err) => {
  console.error(
    'Error fetching compute pipeline tasks (onTasksQueryError):',
    err.message
  );
});

function requestRefetchTasks() {
  // refetch the tasks with the current filters
  refetchTasks(tasksQueryVariables.value)
    ?.catch((err) => {
      console.error('Refetch tasks failed:', err);
    })
    .then(() => {
      console.debug('Refetched tasks');
    });
}

const startingTasksPlaceholder = computed<string | null>(() => {
  // A failed poll keeps the last result, so its count may be outdated.
  if (tasksQueryError.value) return null;
  return getStartingTransferPlaceholder(
    tasksQueryResult.value?.computeTasks?.computeTasks || []
  );
});

// The alert has its own state, so dismissing it keeps the poll error, and
// with it the hidden count, until a poll succeeds.
const showTasksQueryError = ref(false);
watch(tasksQueryError, (err) => {
  showTasksQueryError.value = !!err;
});

// --- Handlers ---
function uploadOptionSelected(option: UploadModeType) {
  // If web upload already selected, just toggle the visibility
  if (selectedUploadOption.value === option) {
    showUploadOptions.value = !showUploadOptions.value;
    return;
  }
  // Otherwise, set the upload mode to WEB and show the options
  selectedUploadOption.value = option;
  showUploadOptions.value = true;
}

function requestRefetchUserDetails() {
  refetchUserDetails()
    ?.catch((err) => {
      console.error('Refetch user failed:', err);
    })
    ?.then(() => {
      console.debug('Refetched user');
    });
}

function onStartedDataTransfer() {
  requestRefetchTasks();

  // collapse the UploadComponent
  showUploadOptions.value = false;

  // show the info message
  showDataTransferStarted.value = true;

  // scroll to ftp transfers list with delay to allow the UploadComponent to collapse
  scrollToElement('heading-data-transfer-list', 500);

  // hide the info message after 5 seconds
  setTimeout(() => {
    showDataTransferStarted.value = false;
  }, 5000);
}
function onStartedWebUpload() {
  // collapse the UploadComponent
  showUploadOptions.value = false;

  // show the info message
  showWebUploadStarted.value = true;

  // scroll to web uploads list with delay to allow the UploadComponent to collapse
  scrollToElement('heading-web-upload-list', 500);

  // hide the info message after 5 seconds
  setTimeout(() => {
    showWebUploadStarted.value = false;
  }, 5000);
}
// onMounted check existing URL parameter for base-space-transfer equals start
onMounted(() => {
  const query = router.currentRoute.value.query;
  if (query['base-space-transfer'] === 'start') {
    onStartedDataTransfer();
  }
});

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("h1", null, [
      _createVNode(_unref(FontAwesomeIcon), { icon: ['fas', 'upload'] }),
      _cache[9] || (_cache[9] = _createTextVNode(" Upload Manager", -1))
    ]),
    _cache[19] || (_cache[19] = _createElementVNode("h5", null, "Track Your Data Uploads", -1)),
    _cache[20] || (_cache[20] = _createElementVNode("hr", null, null, -1)),
    _cache[21] || (_cache[21] = _createElementVNode("p", null, [
      _createTextVNode(" Use this view to monitor the progress of your data transfers and uploads. We support uploading data in "),
      _createElementVNode("strong", null, "FASTQ"),
      _createTextVNode(" formats either directly from your computer or via FTP directly from your sequencing provider. ")
    ], -1)),
    _cache[22] || (_cache[22] = _createElementVNode("p", null, [
      _createElementVNode("strong", null, "Tip:"),
      _createTextVNode(" Keep your files compressed using "),
      _createElementVNode("code", null, "gzip"),
      _createTextVNode(" to reduce bandwidth usage and expedite the upload process. ")
    ], -1)),
    _createVNode(_unref(BAlert), {
      modelValue: _unref(errorFetchUserDetails),
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(errorFetchUserDetails) ? (errorFetchUserDetails).value = $event : null)),
      variant: "danger"
    }, {
      default: _withCtx(() => [
        _createTextVNode(_toDisplayString(_unref(errorFetchUserDetails)) + " ", 1),
        _createVNode(_unref(BButton), {
          variant: "outline-danger",
          onClick: requestRefetchUserDetails,
          size: "sm"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(FontAwesomeIcon), { icon: "sync" }),
            _cache[10] || (_cache[10] = _createTextVNode(" Retry ", -1))
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    (_unref(loadingUserDetails))
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createVNode(_unref(BSpinner), { label: "Loading user details" })
        ]))
      : _createCommentVNode("", true),
    (!userOrganization.value && !_unref(loadingUserDetails))
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(_unref(BAlert), {
            modelValue: userHasNoOrg.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((userHasNoOrg).value = $event)),
            variant: "warning"
          }, {
            default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
              _createTextVNode(" You are not associated with any organization. Please contact your account manager or organization administrator to get access to the upload features. ", -1)
            ]))]),
            _: 1
          }, 8, ["modelValue"])
        ]))
      : _createCommentVNode("", true),
    (userOrganization.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createVNode(_unref(BButton), {
            onClick: _cache[2] || (_cache[2] = ($event: any) => (uploadOptionSelected(_unref(UPLOAD_MODES).WEB))),
            pressed: 
          showUploadOptions.value && selectedUploadOption.value === _unref(UPLOAD_MODES).WEB
        ,
            id: "new-upload-web",
            variant: "outline-dark",
            class: "mb-2 me-2"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(FontAwesomeIcon), {
                icon: ['fas', 'upload'],
                class: "me-1"
              }),
              _cache[12] || (_cache[12] = _createTextVNode(" Upload New Data ", -1))
            ]),
            _: 1
          }, 8, ["pressed"]),
          _createVNode(_unref(BButton), {
            onClick: _cache[3] || (_cache[3] = ($event: any) => (uploadOptionSelected(_unref(UPLOAD_MODES).FTP))),
            pressed: 
          showUploadOptions.value && selectedUploadOption.value === _unref(UPLOAD_MODES).FTP
        ,
            id: "new-upload-ftp",
            variant: "outline-dark",
            class: "mb-2 me-2"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(FontAwesomeIcon), {
                icon: ['fas', 'server'],
                class: "me-1"
              }),
              _cache[13] || (_cache[13] = _createTextVNode(" FTP Server ", -1))
            ]),
            _: 1
          }, 8, ["pressed"]),
          _createVNode(_unref(BButton), {
            onClick: _cache[4] || (_cache[4] = ($event: any) => (uploadOptionSelected(_unref(UPLOAD_MODES).WASABI))),
            pressed: 
          showUploadOptions.value && selectedUploadOption.value === _unref(UPLOAD_MODES).WASABI
        ,
            id: "new-upload-wasabi",
            variant: "outline-dark",
            class: "mb-2 me-2"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(FontAwesomeIcon), {
                icon: ['fas', 'cloud-arrow-down'],
                class: "me-1"
              }),
              _cache[14] || (_cache[14] = _createTextVNode(" Wasabi S3 ", -1))
            ]),
            _: 1
          }, 8, ["pressed"]),
          _createVNode(_component_router_link, { to: { name: 'BaseSpaceView' } }, {
            default: _withCtx(() => [
              _createVNode(_unref(BButton), {
                variant: "outline-dark",
                class: "mb-2 me-2",
                id: "connect-basespace-button"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(FontAwesomeIcon), {
                    icon: "info-circle",
                    class: "me-1"
                  }),
                  _cache[15] || (_cache[15] = _createTextVNode(" Illumina BaseSpace ", -1))
                ]),
                _: 1
              }),
              _createVNode(_unref(BTooltip), { target: "connect-basespace-button" }, {
                default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                  _createTextVNode(" Connect to Illumina BaseSpace Sequence Hub to upload data directly from your projects. ", -1)
                ]))]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_unref(BCollapse), {
            modelValue: showUploadOptions.value,
            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((showUploadOptions).value = $event)),
            id: "new-upload"
          }, {
            default: _withCtx(() => [
              _createVNode(UploadComponent, {
                "selected-upload-option": selectedUploadOption.value,
                onStartedDataTransfer: onStartedDataTransfer,
                onStartedWebUploads: onStartedWebUpload
              }, null, 8, ["selected-upload-option"])
            ]),
            _: 1
          }, 8, ["modelValue"])
        ]))
      : _createCommentVNode("", true),
    _cache[23] || (_cache[23] = _createElementVNode("h5", {
      id: "heading-data-transfer-list",
      class: "mt-4"
    }, "Data Transfer Tasks", -1)),
    _cache[24] || (_cache[24] = _createElementVNode("p", null, " Once initiated, the file transfer continues automatically. You can safely navigate away without interrupting it, and will continue even if you leave or close the app. ", -1)),
    _createVNode(_unref(BAlert), {
      modelValue: showDataTransferStarted.value,
      "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((showDataTransferStarted).value = $event)),
      variant: "info"
    }, {
      default: _withCtx(() => [
        _cache[17] || (_cache[17] = _createTextVNode(" Data transfer registered and started successfully. ", -1)),
        _createVNode(_unref(FontAwesomeIcon), { icon: "check" })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_unref(BAlert), {
      modelValue: showTasksQueryError.value,
      "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((showTasksQueryError).value = $event)),
      variant: "danger",
      dismissible: ""
    }, {
      default: _withCtx(() => [
        _createTextVNode(_toDisplayString(_unref(tasksQueryError)), 1)
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(ComputeTaskList, {
      "parent-name": "Upload Manager",
      placeholder: startingTasksPlaceholder.value,
      "user-role": _unref(SHARING_USER_ROLES).OWNER,
      "task-types": _unref(TRANSFER_TASK_TYPES),
      "task-name-column-title": "Transfer"
    }, null, 8, ["placeholder", "user-role", "task-types"]),
    _cache[25] || (_cache[25] = _createElementVNode("h5", {
      id: "heading-web-upload-list",
      class: "mt-4"
    }, "Active Direct Uploads", -1)),
    _cache[26] || (_cache[26] = _createElementVNode("p", null, " Your file is uploaded directly from this browser session. You may continue browsing data, samples, projects, and analysis. However, avoid closing or refreshing the browser during the upload. If interrupted, your progress is saved so you can resume the upload without losing data within 7 days. ", -1)),
    _createVNode(_unref(BAlert), {
      modelValue: showWebUploadStarted.value,
      "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((showWebUploadStarted).value = $event)),
      variant: "info"
    }, {
      default: _withCtx(() => [
        _cache[18] || (_cache[18] = _createTextVNode(" Upload registered and started successfully. ", -1)),
        _createVNode(_unref(FontAwesomeIcon), { icon: "check" })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(WebUploadList)
  ]))
}
}

})